import api from 'services/api';
import { enforce, nullable, num, object, runSchema, str } from 'validation';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';

/**
 * The request body for both `POST /locks` (create) and `PUT /locks/:zone/:name`
 * (update). **Permissive on purpose** — the server is the validation authority
 * (name pattern, positive limit, zone/folder existence). A too-strict client
 * type would block valid sends when backend rules loosen; the FE authors this
 * body, so there is no response to validate and nothing to drift.
 *
 * Shared by POST and PUT because the FE sends the same shape to both. On update
 * the backend treats each field as "change to this value" (an omitted field is
 * left untouched); the edit model always sends the full set, so every field is
 * written from the form.
 */
export interface SaveLock {
  readonly zone_name: string | null;
  readonly name: string | null;
  readonly limit: number | null;
  readonly description: string | null;
  readonly folder_id: number | null;
}

// POST /locks — create. Backend returns the `INSERT … RETURNING` row (which
// uniquely includes `zone_id`) with `zone_name` echoed from the request and
// `folder_path` from the shared serializer.
//
// `CreatedLock` is its OWN type + schema, even though it overlaps the read
// shapes: it is produced by a distinct backend statement (equal-by-coincidence,
// not identity — see ../../README.md). Structural typing lets it flow wherever a
// `Lock` is expected downstream.
export interface CreatedLock {
  readonly id: number;
  readonly name: string;
  readonly description: string | null;
  readonly zone_id: number;
  readonly limit: number;
  readonly limit_allocation: number;
  readonly created_at: string;
  readonly updated_at: string;
  readonly folder_id: number | null;
  readonly zone_name: string;
  readonly folder_path: FolderPath | null;
}

const createdSchema = object<CreatedLock>({
  id: num(),
  name: str(),
  description: nullable(str()),
  zone_id: num(),
  limit: num(),
  limit_allocation: num(),
  created_at: str(),
  updated_at: str(),
  folder_id: nullable(num()),
  zone_name: str(),
  folder_path: nullable(folderPathSchema),
});

/**
 * POST `/locks`. Returns the created record. Throws on HTTP error (caller
 * handles the 400 path) or on structural validation failure of the response.
 */
export const postLock = async (save: SaveLock): Promise<CreatedLock> => {
  const res = await api.post('/locks', save);
  const r = runSchema(createdSchema, res.data, 'postLock');
  if (!r.ok) {
    throw new Error('postLock: response failed validation');
  }
  return enforce(r.value, r.warnings, ['id']);
};
